import React, { PureComponent, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { withTranslation } from 'react-i18next';

import { makeGetCurrentUser } from '../selectors/users';
import { makeGetSortedActiveProjectTaskGroups } from '../selectors/task-groups';

import { createAlert } from 'services/alert-service';
import { encodeCronString, decodeCronString } from 'utils/recurring';
import { altDateFormat } from 'utils/date';

import { LocaleContext } from 'context/LocaleContext';
import BodyPortal from 'portals/body-portal';

import Popout from './popout';
import Select from './select';
import FieldDefault from './field-default';
import Checkbox from './checkbox';
import SelectTaskGroupPopout from './SelectTaskGroupPopout';
import LocalizedDatePicker from './LocalizedDatePicker';

import IconClose from '../icons/icon-close';
import IconArrowDown from '../icons/icon-arrow-down';

const convertTo12h = time => {
    let [hours, minutes] = time.split(':');

    hours = Number(hours);

    if (hours > 12) {
        hours = hours - 12;
    }

    if (hours === 0) {
        hours = 12;
    }

    return `${hours.toString().padStart(2, '0')}:${minutes.padStart(2, '0')}`;
}

const TypeSelector = ({ type, onChange, t }) => {
    const cronTypes = useMemo(() => [
        {
            id: 'day',
            text: t('cronTypes.day')
        },
        {
            id: 'week',
            text: t('cronTypes.week')
        },
        {
            id: 'month',
            text: t('cronTypes.month')
        },
        {
            id: 'period',
            text: t('cronTypes.period')
        },
        {
            id: 'year',
            text: t('cronTypes.year')
        }
    ], [t]);

    useEffect(() => {
        if (!type) {
            onChange(cronTypes[0]);
        }
    }, [type, cronTypes, onChange]);

    return (
        <div className="resource-repeat-row">
            <div className="row-label">
                { t('Every (period type)') }
            </div>

            <div className="row-value">
                <div className="row-input">
                    <Select
                        small={true}
                        selected={type}
                        options={cronTypes}
                        onChange={onChange}
                    />
                </div>
            </div>
        </div>
    );
}

const BiWeeklySelector = ({ biWeekly, currentWeekIsOdd, onChange, t }) => {
    const handleChange = useCallback(({ id }) => {
        if ((currentWeekIsOdd && id === 'currentWeek') || (!currentWeekIsOdd && id === 'nextWeek')) {
            onChange(1);
        } else {
            onChange(2);
        }
    }, [onChange]);

    const biWeeklyType = useMemo(() => {
        if (currentWeekIsOdd) {
            if (biWeekly === 1) {
                return 'currentWeek';
            }
        } else {
            if (biWeekly === 2) {
                return 'currentWeek';
            }
        }

        return 'nextWeek';
    }, [biWeekly]);

    const biWeeklyTypes = useMemo(() => [
        {
            id: 'currentWeek',
            text: t('cronBiWeekly.This week')
        },
        {
            id: 'nextWeek',
            text: t('cronBiWeekly.Next week')
        }
    ], [t]);

    return (
        <div className="resource-repeat-row">
            <div className="row-label">
                { t('Starts (this/next week)') }
            </div>

            <div className="row-value">
                <div className="row-input">
                    <Select
                        small={true}
                        selected={biWeeklyType}
                        options={biWeeklyTypes}
                        onChange={handleChange}
                    />
                </div>
            </div>
        </div>
    );
}

const PeriodSelector = ({ month, onChange, t }) => {
    const cronPeriods = useMemo(() => [
        {
            id: 'biWeekly',
            text: t('cronPeriods.bi-weekly')
        },
        {
            id: '*/2',
            text: t('cronPeriods.two months')
        },
        {
            id: '*/3',
            text: t('cronPeriods.quarterly')
        },
        {
            id: '*/4',
            text: t('cronPeriods.four months')
        },
        {
            id: '*/6',
            text: t('cronPeriods.bi-annually')
        }
    ], [t]);

    return (
        <div className="resource-repeat-row">
            <div className="row-label">
                { t('In (year period)') }
            </div>

            <div className="row-value">
                <div className="row-input">
                    <Select
                        small={true}
                        selected={month}
                        options={cronPeriods}
                        onChange={onChange}
                    />
                </div>
            </div>
        </div>
    );
}

const MonthSelector = ({ month, onChange, t }) => {
    const months = useMemo(() => [
        {
            id: '1',
            text: t('date:months.January')
        },
        {
            id: '2',
            text: t('date:months.February')
        },
        {
            id: '3',
            text: t('date:months.March')
        },
        {
            id: '4',
            text: t('date:months.April')
        },
        {
            id: '5',
            text: t('date:months.May')
        },
        {
            id: '6',
            text: t('date:months.June')
        },
        {
            id: '7',
            text: t('date:months.July')
        },
        {
            id: '8',
            text: t('date:months.August')
        },
        {
            id: '9',
            text: t('date:months.September')
        },
        {
            id: '10',
            text: t('date:months.October')
        },
        {
            id: '11',
            text: t('date:months.November')
        },
        {
            id: '12',
            text: t('date:months.December')
        }
    ], [t]);

    return (
        <div className="resource-repeat-row">
            <div className="row-label">
                { t('In (month)') }
            </div>

            <div className="row-value">
                <div className="row-input">
                    <Select
                        small={true}
                        selected={month}
                        options={months}
                        onChange={onChange}
                    />
                </div>
            </div>
        </div>
    );
}

const MonthDaySelector = ({ monthDay, onChange, t }) => {
    const handleBlur = value => {
        if (isNaN(value)) {
            value = 1;
        }

        if (value - 0 < 1) {
            value = 1;
        }

        if (value - 0 > 31) {
            value = 31;
        }

        onChange(value);
    }

    return (
        <div className="resource-repeat-row">
            <div className="row-label">
                { t('Date of (month day)') }
            </div>

            <div className="row-value">
                <div className="row-input">
                    <FieldDefault
                        small={true}
                        value={monthDay}
                        onBlur={handleBlur}
                        onChange={onChange}
                    />
                </div>

                <div className="row-input">
                    <small className="input-helper">
                        ({ t('monthDayHelper') })
                    </small>
                </div>
            </div>
        </div>
    );
}

const WeekDaysSelector = ({ dayOfWeek, onDayClick, t }) => {
    const weekDays = useMemo(() => [
        {
            id: '1',
            text: t('date:weekDays.Mon Short')
        },
        {
            id: '2',
            text: t('date:weekDays.Tue Short')
        },
        {
            id: '3',
            text: t('date:weekDays.Wed Short')
        },
        {
            id: '4',
            text: t('date:weekDays.Thu Short')
        },
        {
            id: '5',
            text: t('date:weekDays.Fri Short')
        },
        {
            id: '6',
            text: t('date:weekDays.Sat Short')
        },
        {
            id: '0',
            text: t('date:weekDays.Sun Short')
        }
    ], [t]);

    useEffect(() => {
        if (!dayOfWeek.length) {
            onDayClick(weekDays[0].id);
        }
    }, [dayOfWeek, weekDays, onDayClick]);

    return (
        <div className="resource-repeat-row">
            <div className="row-label">
                { t('On (week day)') }
            </div>

            <div className="row-value">
                <div className="row-input">
                    <div className="row-input-button-group">
                        {
                            weekDays.map(day => <CronWeekDayButton
                                key={day.id}
                                id={day.id}
                                text={day.text}
                                selected={dayOfWeek.includes(day.id)}
                                onClick={onDayClick}
                            />)
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}

const StartAtSelector = ({ startAt, onChange, t }) => {
    const locale = useContext(LocaleContext);
    const fieldCtrl = useRef(null);

    const midnightOptions = useMemo(() => [
        {
            id: 'am',
            text: t('date:AM')
        },
        {
            id: 'pm',
            text: t('date:PM')
        }
    ], [t]);

    const [startAtHour] = startAt.split(':');
    const [startAtMidnight, setStartAtMidnight] = useState(startAtHour ? moment(Number(startAtHour), 'HH').format('a') : midnightOptions[0].id);
    const startAtString = useMemo(() => {
        if (locale.timeFormat !== '12h') {
            return startAt;
        }

        if (!startAt.match(/^\d{1,2}:\d{1,2}$/)) {
            return startAt;
        }

        return convertTo12h(startAt);
    }, [startAt, startAtMidnight, locale]);

    const updateTime = useCallback((nextStartAt, nextStartAtMidnight) => {
        if (nextStartAt.match(/^\d{4}$/)) {
            const [h1, h2, m1, m2] = nextStartAt.split('');
            nextStartAt = `${h1}${h2}:${m1}${m2}`;
        }

        if (!nextStartAt.match(/^\d{0,2}:\d{0,2}$/)) {
            nextStartAt = '09:00';
        }

        let [hours, minutes] = nextStartAt.split(':');
        hours = Number(hours);

        if (locale.timeFormat === '12h') {
            if (hours === 0) {
                hours = 12;
            }

            if (nextStartAtMidnight === 'pm') {
                if (hours !== 12) {
                    hours = (hours + 12).toString();
                }
            } else {
                if (hours >= 12) {
                    if (hours > 12) {
                        setStartAtMidnight('pm');
                    }

                    hours = (hours - 12).toString();
                }
            }
        }

        if (hours - 0 > 23) {
            hours = 23;
        }

        if (Number(minutes) > 60) {
            minutes = '60';
        }

        nextStartAt = `${hours.toString().padStart(2, '0')}:${minutes.padStart(2, '0')}`;

        if (fieldCtrl.current) {
            if (locale.timeFormat === '12h') {
                if (convertTo12h(nextStartAt) !== fieldCtrl.current.getValue()) {
                    fieldCtrl.current.setValue(convertTo12h(nextStartAt));
                }
            } else {
                if (nextStartAt !== fieldCtrl.current.getValue()) {
                    fieldCtrl.current.setValue(nextStartAt);
                }
            }
        }

        onChange(nextStartAt);
    }, [locale, fieldCtrl, startAtString, onChange])

    const handleBlur = useCallback(nextStartAt => {
        updateTime(nextStartAt, startAtMidnight);
    }, [startAtMidnight, updateTime]);

    const handleMidnightChange = useCallback(({ id }) => {
        setStartAtMidnight(id);

        updateTime(startAt, id);
    }, [startAt, updateTime]);

    return (
        <div className="resource-repeat-row">
            <div className="row-label">
                { t('At (time)') }
            </div>

            <div className="row-value">
                <div className="row-input">
                    <FieldDefault
                        small={true}
                        value={startAtString}
                        onBlur={handleBlur}
                        provideController={fieldCtrl}
                    />
                </div>

                {
                    locale.timeFormat === '12h' ? (
                        <div className="row-input">
                            <Select
                                small={true}
                                selected={startAtMidnight}
                                options={midnightOptions}
                                onChange={handleMidnightChange}
                            />
                        </div>
                    ) : null
                }
            </div>
        </div>
    )
}

const TillSelector = ({ till, onChange, t }) => {
    const locale = useContext(LocaleContext);

    return (
        <div className="resource-repeat-row">
            <div className="row-label">
                { t('Ends (date)') }
            </div>

            <div className="row-value">
                <div className="row-input">
                    <LocalizedDatePicker
                        selected={till}
                        showTimeSelect={true}
                        minDate={new Date()}
                        dateFormat={altDateFormat(locale.dateTimeFormats.fullDateTime)}
                        placeholderText={ t('common:never') }
                        customInput={<DatePickerInput />}
                        popperContainer={BodyPortal}
                        onChange={onChange}
                    />
                </div>
            </div>
        </div>
    )
}

const CronWeekDayButton = ({ id, text, selected, onClick }) => {
    const handleClick = () => onClick && onClick(id);

    return <button className={'btn btn-input' + (selected ? ' selected' : '')} onClick={handleClick}>{text}</button>;
}

const DatePickerInput = ({ value, placeholder, onClick }) => <button className="btn btn-input wide" onClick={onClick}>{value || placeholder} <IconArrowDown /></button>;

class ResourceRepeatPopout extends PureComponent {
    static defaultProps = {
        trigger: null,
        till: null,
        types: ['date', 'status', 'completion']
    }

    state = {
        type: this.props.type || this.types[0].id,
        cron: this.props.cron || this.defaultCron,
        ...decodeCronString({
            cronString: this.props.cron,
            cronTZOffset: this.props.tz_offset,
            biWeekly: this.props.bi_weekly
        }),
        trigger: this.props.trigger || (this.props.taskGroups.length ? this.props.taskGroups[0].id : null),
        till: this.props.till ? new Date(this.props.till) : null,
        popoutVisible: false
    }

    handlePopoutOpenClick = () => {
        this.show();
    }

    handlePopoutCloseClick = () => {
        this.popoutCtrl.hide();
    }

    handlePopoutShow = () => {
        this.setState({
            popoutVisible: true
        });

        this.props.onShow && this.props.onShow();
    }

    handlePopoutHide = () => {
        this.setState({
            popoutVisible: false
        });

        this.props.onHide && this.props.onHide();
    }

    handleTypeChange = ({ id }) => {
        this.setState({
            type: id
        });
    }

    handleCronTypeChange = ({ id }) => {
        const cronDayOfWeek = (id === 'week' && this.state.cronDayOfWeek.length > 1) ? [this.state.cronDayOfWeek[0]] : this.state.cronDayOfWeek;
        const cronMonth = id === 'year' ? '1' : '';

        this.setState({
            cronType: id,
            cronMonth,
            cronDayOfWeek,
            cronBiWeekly: id === 'period' ? (this.currentWeekIsOdd ? 1 : 2) : 0
        });
    }

    handleBiWeeklyChange = cronBiWeekly => {
        this.setState({ cronBiWeekly });
    }

    handleCronWeekDayClick = cronWeekDayId => {
        if (this.state.cronType === 'week' || this.state.cronBiWeekly) {
            this.setState({
                cronDayOfWeek: [cronWeekDayId]
            });
            return;
        }

        this.setState({
            cronDayOfWeek: this.state.cronDayOfWeek.includes(cronWeekDayId) ? this.state.cronDayOfWeek.filter(id => id !== cronWeekDayId) : [...this.state.cronDayOfWeek, cronWeekDayId]
        });
    }

    handleCronMonthDayChange = cronDayOfMonth => {
        this.setState({ cronDayOfMonth });
    }

    handleCronPeriodChange = ({ id }) => {
        if (id === 'biWeekly') {
            this.setState({ cronMonth: '*', cronBiWeekly: this.currentWeekIsOdd ? 1 : 2 });
        } else {
            this.setState({ cronMonth: id, cronBiWeekly: 0 });
        }
    }

    handleCronMonthChange = ({ id }) => {
        this.setState({ cronMonth: id });
    }

    handleCronStartAtChange = cronStartAt => {
        this.setState({ cronStartAt });
    }

    handleTriggerChange = ({ id }) => {
        this.setState({
            trigger: id
        });
    }

    handleTillChange = till => {
        this.setState({ till });
    }

    handleDisableRepeat = () => {
        this.props.onDisable && this.props.onDisable();

        this.popoutCtrl.hide();
    }

    handleSubmit = () => {
        if (this.state.type === 'date') {
            const cron = encodeCronString(this.state);
            if (cron.error) {
                createAlert('default', cron.message);
                return;
            }

            this.props.onChange({
                type: this.state.type,
                cron,
                till: this.state.till,
                bi_weekly: this.state.cronBiWeekly
            });
        } else {
            this.props.onChange({
                type: this.state.type,
                trigger: this.state.type === 'status' ? this.state.trigger : undefined,
                cron: null,
                till: null
            });
        }

        this.popoutCtrl.hide();
    }

    handleCancel = () => {
        this.popoutCtrl.hide();

        this.props.onCancel && this.props.onCancel();
    }

    show() {
        this.popoutCtrl.show();
    }

    setPopoutCtrl = ctrl => this.popoutCtrl = ctrl;

    get defaultCron() {
        return `0 ${((this.props.currentUser && this.props.currentUser.tz_offset) || 0) / 60 + 9} * * *`;
    }

    get types() {
        const { t } = this.props;

        return [
            {
                id: 'date',
                text: t('types.date')
            },
            {
                id: 'status',
                text: t('types.status')
            },
            {
                id: 'completion',
                text: t('types.completion')
            }
        ];
    }

    get filteredTypes() {
        return this.types.filter(type => this.props.types.includes(type.id));
    }

    get currentWeekIsOdd() {
        return Boolean(moment().week() % 2)
    }

    componentDidUpdate(prevProps) {
        if (prevProps.cron !== this.props.cron || prevProps.tz_offset !== this.props.tz_offset || prevProps.bi_weekly !== this.props.bi_weekly) {
            const cron = this.props.cron || this.defaultCron;
            this.setState({
                cron,
                ...decodeCronString({
                    cronString: cron,
                    cronTZOffset: this.props.tz_offset,
                    biWeekly: this.props.bi_weekly
                })
            });
        }

        if (prevProps.type !== this.props.type) {
            this.setState({
                type: this.props.type || this.types[0].id
            });
        }

        if (prevProps.till !== this.props.till) {
            this.setState({
                till: this.props.till ? new Date(this.props.till) : null
            });
        }
    }

    render() {
        // TODO: Add content loader
        const children = React.Children.map(this.props.children, child => {
            return React.cloneElement(child, {
                className: child.props.className + (this.state.popoutVisible ? ' selected disable-tooltip' : '')
            });
        });

        const { t } = this.props;

        return (
            <React.Fragment>
                <div onClick={this.handlePopoutOpenClick}>
                    {children}
                </div>

                <Popout
                    className="no-arrow auto-size"
                    provideController={this.setPopoutCtrl}
                    onShow={this.handlePopoutShow}
                    onHide={this.handlePopoutHide}
                    xAlign={this.props.xAlign}
                    defaultXPosition={this.props.defaultXPosition}
                    closeOnEveryClick={true}
                    visible={this.props.autoShow}
                    inline={this.props.inline}
                >
                    {
                        this.props.title ? (
                            <div className="popout-head">
                                { this.props.title }

                                <button className="btn btn-icon btn-small popout-close" onClick={this.handlePopoutCloseClick}>
                                    <IconClose />
                                </button>
                            </div>
                        ) : null
                    }

                    <div className="popout-main">
                        <div className="resource-repeat">
                            {
                                this.props.types.length > 1 ? (
                                    <div className="resource-repeat-row">
                                        <div className="row-label">
                                            { t('Based on') }
                                        </div>

                                        <div className="row-value">
                                            <div className="row-input">
                                                <Select
                                                    small={true}
                                                    selected={this.state.type}
                                                    options={this.filteredTypes}
                                                    onChange={this.handleTypeChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : null
                            }

                            {
                                this.state.type === 'date' ? (
                                    <React.Fragment>
                                        <TypeSelector
                                            type={this.state.cronType}
                                            onChange={this.handleCronTypeChange}
                                            t={t}
                                        />

                                        {
                                            this.state.cronType === 'period' ? (
                                                <React.Fragment>
                                                    <PeriodSelector
                                                        month={this.state.cronBiWeekly ? 'biWeekly' : this.state.cronMonth}
                                                        onChange={this.handleCronPeriodChange}
                                                        t={t}
                                                    />

                                                    {
                                                        this.state.cronBiWeekly ? (
                                                            <React.Fragment>
                                                                <WeekDaysSelector
                                                                    dayOfWeek={this.state.cronDayOfWeek}
                                                                    onDayClick={this.handleCronWeekDayClick}
                                                                    t={t}
                                                                />

                                                                <BiWeeklySelector
                                                                    biWeekly={this.state.cronBiWeekly}
                                                                    currentWeekIsOdd={this.currentWeekIsOdd}
                                                                    onChange={this.handleBiWeeklyChange}
                                                                    t={t}
                                                                />
                                                            </React.Fragment>
                                                        ) : (
                                                            <MonthDaySelector
                                                                monthDay={this.state.cronDayOfMonth}
                                                                onChange={this.handleCronMonthDayChange}
                                                                t={t}
                                                            />
                                                        )
                                                    }
                                                </React.Fragment>
                                            ) : this.state.cronType === 'year' ? (
                                                <React.Fragment>
                                                    <MonthSelector
                                                        month={this.state.cronMonth}
                                                        onChange={this.handleCronMonthChange}
                                                        t={t}
                                                    />

                                                    <MonthDaySelector
                                                        monthDay={this.state.cronDayOfMonth}
                                                        onChange={this.handleCronMonthDayChange}
                                                        t={t}
                                                    />
                                                </React.Fragment>
                                            ) : (
                                                <React.Fragment>
                                                    {
                                                        this.state.cronType === 'month' ? (
                                                            <MonthDaySelector
                                                                monthDay={this.state.cronDayOfMonth}
                                                                onChange={this.handleCronMonthDayChange}
                                                                t={t}
                                                            />
                                                        ) : (
                                                            <WeekDaysSelector
                                                                dayOfWeek={this.state.cronDayOfWeek}
                                                                onDayClick={this.handleCronWeekDayClick}
                                                                t={t}
                                                            />
                                                        )
                                                    }
                                                </React.Fragment>
                                            )
                                        }

                                        <StartAtSelector
                                            startAt={this.state.cronStartAt}
                                            onChange={this.handleCronStartAtChange}
                                            t={t}
                                        />

                                        <TillSelector
                                            till={this.state.till}
                                            onChange={this.handleTillChange}
                                            t={t}
                                        />
                                    </React.Fragment>
                                ) : this.state.type === 'completion' ? (
                                    <React.Fragment>
                                        <div className="resource-repeat-row">
                                            { t('When the task is marked completed') }
                                        </div>
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <div className="resource-repeat-row">
                                            <div className="row-label">
                                                { t('is set to') }
                                            </div>

                                            <div className="row-value">
                                                <div className="row-input">
                                                    <div className="select small">
                                                        <SelectTaskGroupPopout
                                                            selectClassName="select-toggle"
                                                            xAlign="right"
                                                            defaultXPosition="left"
                                                            withProject={false}
                                                            withHead={false}
                                                            projectId={this.props.projectId}
                                                            selected={this.state.trigger}
                                                            onSelect={this.handleTriggerChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                )
                            }
                        </div>
                    </div>

                    <div className="popout-foot">
                        {
                            this.props.rule ? (
                                <span className="action" onClick={this.handleDisableRepeat}>{ t('common:Disable') }</span>
                            ) : null
                        }

                        <div className="popout-actions">
                            <button className="btn btn-secondary btn-small" onClick={this.handleCancel}>{ t('common:Cancel') }</button>

                            <button className="btn btn-success btn-small" onClick={this.handleSubmit}>
                                { t('Set recurring') }
                            </button>
                        </div>
                    </div>
                </Popout>
            </React.Fragment>
        );
    }
}

const makeMapStateToProps = () => {
    const getCurrentUser = makeGetCurrentUser();
    const getSortedActiveProjectTaskGroups = makeGetSortedActiveProjectTaskGroups();

    return ({ users, taskGroups }, ownProps) => {

        return {
            currentUser: getCurrentUser(users),
            taskGroups: ownProps.projectId ? getSortedActiveProjectTaskGroups(taskGroups.items, ownProps.projectId) : []
        }
    }
}

export default connect(makeMapStateToProps)(withTranslation(['recurring', 'date', 'common'])(ResourceRepeatPopout));
